// src/components/AccountPickerMulti.tsx
import React, { useState } from 'react';
import { Box, Typography, TextField, Button, MenuItem, Checkbox, Chip } from '@mui/material';

export interface AccountOption {
  id: string;
  name: string;
  phone_number?: string;
}

export interface AccountGroup {
  id: string;
  name: string;
  linked_accounts: AccountOption[];
}

interface Props {
  accounts: AccountOption[];
  selected: string[];           // массив name
  onChange: (names: string[]) => void;
  groups?: AccountGroup[];      // опционально — для быстрого выбора по группе
  placeholder?: string;
}

const AccountPickerMulti: React.FC<Props> = ({
  accounts,
  selected,
  onChange,
  groups,
  placeholder = 'Выберите аккаунты',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = accounts.filter(acc =>
    acc.name.toLowerCase().includes(search.toLowerCase()) ||
    acc.phone_number?.includes(search)
  ).slice(0, 50);

  const filteredNames = filtered.map(a => a.name);
  const allFilteredSelected = filteredNames.length > 0 && filteredNames.every(n => selected.includes(n));
  const totalFiltered = accounts.filter(acc =>
    acc.name.toLowerCase().includes(search.toLowerCase()) ||
    acc.phone_number?.includes(search)
  ).length;

  const toggle = (name: string) =>
    onChange(selected.includes(name) ? selected.filter(n => n !== name) : [...selected, name]);

  const toggleAllFiltered = () => {
    if (allFilteredSelected) onChange(selected.filter(n => !filteredNames.includes(n)));
    else onChange([...selected, ...filteredNames.filter(n => !selected.includes(n))]);
  };

  // Добавить все аккаунты группы к уже выбранным
  const applyGroup = (group: AccountGroup) => {
    const names = group.linked_accounts.map(a => a.name);
    const toAdd = names.filter(n => !selected.includes(n));
    if (toAdd.length > 0) onChange([...selected, ...toAdd]);
  };

  // Заменить выбранное на аккаунты группы
  const replaceWithGroup = (group: AccountGroup) => {
    onChange(group.linked_accounts.map(a => a.name));
  };

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      {/* Триггер */}
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          width: '100%',
          border: open ? '2px solid #1E40AF' : '1px solid #C4C4C4',
          borderRadius: 1, px: 1.5, py: 1, minHeight: 56, cursor: 'pointer',
          display: 'flex', alignItems: 'center', boxSizing: 'border-box', bgcolor: 'white',
          '&:hover': { borderColor: '#1E40AF' },
        }}
      >
        {selected.length === 0
          ? <Typography color="text.secondary">{placeholder}</Typography>
          : <Typography sx={{ color: '#1E40AF', fontWeight: 500 }}>
              Выбрано {selected.length} из {accounts.length} аккаунтов
            </Typography>
        }
      </Box>

      {open && (
        <>
          {/* Оверлей */}
          <Box
            onClick={() => { setOpen(false); setSearch(''); }}
            sx={{ position: 'fixed', inset: 0, zIndex: 1200 }}
          />

          <Box sx={{
            position: 'absolute', top: '100%', left: 0, width: '100%', zIndex: 1300,
            bgcolor: 'white', border: '1px solid #E5E7EB', borderRadius: 1,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)', mt: 0.5,
            display: 'flex', flexDirection: 'column', maxHeight: 520,
          }}>

            {/* Строка поиска + кнопки */}
            <Box sx={{ p: 1.5, borderBottom: '1px solid #E5E7EB', display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                autoFocus size="small" sx={{ flex: 1 }}
                placeholder="Поиск по имени или номеру..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onClick={e => e.stopPropagation()}
              />
              <Button size="small" variant="outlined"
                onClick={e => { e.stopPropagation(); toggleAllFiltered(); }}
                sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontSize: '0.78rem' }}>
                {allFilteredSelected ? 'Снять отфильтр.' : `Выбрать все (${filtered.length})`}
              </Button>
              {selected.length > 0 && (
                <Button size="small" color="error" variant="outlined"
                  onClick={e => { e.stopPropagation(); onChange([]); }}
                  sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontSize: '0.78rem' }}>
                  Снять все
                </Button>
              )}
            </Box>

           {/* Быстрый выбор по группам */}
{groups && groups.length > 0 && (
  <Box sx={{
    px: 2,
    py: 1.5,
    borderBottom: '1px solid #E5E7EB',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
    alignItems: 'center',
    bgcolor: '#EFF6FF',          // светлый синий фон для блока
    borderRadius: 1,
  }}>
    <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 1, fontWeight: 600 }}>
      Группы:
    </Typography>
    {groups.map((group, idx) => {
      // Можно циклически менять цвета для разных групп
      const colors = ['#DBEAFE', '#D1FAE5', '#FEF3C7', '#FEE2E2', '#EDE9FE'];
      const color = colors[idx % colors.length];
      return (
        <Chip
          key={group.id}
          label={`${group.name} (${group.linked_accounts.length})`}
          size="medium"
          variant="filled"
          onClick={e => { e.stopPropagation(); applyGroup(group); }}
          onDelete={e => { e.stopPropagation(); replaceWithGroup(group); }}
          deleteIcon={
            <Box component="span" sx={{ fontSize: '0.75rem', px: 0.25 }} title="Заменить выбор на эту группу">
              ↺
            </Box>
          }
          sx={{
            cursor: 'pointer',
            bgcolor: color,
            '&:hover': { bgcolor: '#BFDBFE' },
            fontWeight: 500,
            fontSize: '0.85rem',
          }}
        />
      );
    })}
    <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, fontStyle: 'italic' }}>
      клик — добавить, ↺ — заменить
    </Typography>
  </Box>
)}
            {/* Список аккаунтов — 2 колонки */}
            <Box sx={{ overflowY: 'auto', flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', alignContent: 'start' }}>
              {filtered.length === 0
                ? <Typography sx={{ p: 2, color: 'text.secondary', textAlign: 'center', gridColumn: '1 / -1' }}>
                    Ничего не найдено
                  </Typography>
                : filtered.map(acc => (
                  <MenuItem key={acc.id} dense
                    onClick={e => { e.stopPropagation(); toggle(acc.name); }}
                    selected={selected.includes(acc.name)}
                    sx={{ py: 0.75, px: 1.5 }}>
                    <Checkbox size="small" checked={selected.includes(acc.name)} sx={{ p: 0.5, mr: 1 }} />
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography variant="body2" fontWeight={selected.includes(acc.name) ? 600 : 400} noWrap>
                        {acc.name}
                      </Typography>
                      {acc.phone_number && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {acc.phone_number}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))
              }
              {totalFiltered > 50 && (
                <Typography sx={{ p: 1.5, fontSize: '0.75rem', color: '#6B7280', textAlign: 'center', gridColumn: '1 / -1' }}>
                  Показаны первые 50 — уточните поиск
                </Typography>
              )}
            </Box>

            {/* Футер */}
            <Box sx={{ px: 2, py: 1, borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', bgcolor: '#F9FAFB' }}>
              <Typography variant="caption" color="text.secondary">
                {search ? `Найдено: ${totalFiltered}` : `Всего: ${accounts.length}`}
              </Typography>
              <Typography variant="caption" fontWeight={600} color="#1E40AF">
                Выбрано: {selected.length}
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default AccountPickerMulti;